import { useState } from 'react';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import { Pagination } from './ui/Pagination';

export interface SessionRow {
  id: string;
  title: string;
  statusText: string;
  statusBadge: string; // tailwind bg+text classes, e.g. 'bg-yellow-50 text-yellow-700'
  statusDot?: string; // tailwind bg classes, e.g. 'bg-yellow-400'
  projectName?: string;
  createdAt: string;
  updatedAt?: string;
  meta?: string; // small muted line, e.g. 'Started by Josh'
  onDelete?: () => void; // if set, a trash button shows on row hover
}

export interface SessionSection {
  key: string;
  title: string;
  rows: SessionRow[];
  emptyText?: string;
}

export interface SessionListPanelProps {
  sections: SessionSection[]; // ordered — rendered in order
  activeId?: string;
  onSelect: (id: string) => void;
  onNewSession?: () => void; // if set, render a New Session button above the sections
  newSessionLabel?: string; // default 'New Session'
  showProject?: boolean; // show row.projectName (cross-project mode)
  loading?: boolean;
  perPage?: number; // default 5
  emptyText?: string; // shown when every section is empty and not loading
  className?: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function SessionRowItem({
  row,
  active,
  onSelect,
  showProject,
}: {
  row: SessionRow;
  active: boolean;
  onSelect: (id: string) => void;
  showProject?: boolean;
}) {
  return (
    <div
      onClick={() => onSelect(row.id)}
      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors group border-t border-slate-100 first:border-t-0 ${
        active ? 'bg-primary-light' : 'hover:bg-slate-50'
      }`}
    >
      {/* Status dot */}
      {row.statusDot && (
        <div className="mt-2 shrink-0">
          <div className={`w-2 h-2 rounded-full ${row.statusDot}`} />
        </div>
      )}

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${row.statusBadge}`}>
            {row.statusText}
          </span>
          <span className="text-xs text-gray-500">{formatDate(row.updatedAt || row.createdAt)}</span>
          {showProject && row.projectName && (
            <span className="text-xs bg-slate-100 text-gray-500 px-2.5 py-0.5 rounded-full">
              {row.projectName}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-700 line-clamp-2 mb-1">{row.title}</p>
        {row.meta && (
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>{row.meta}</span>
          </div>
        )}
      </div>

      {/* Delete */}
      {row.onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            row.onDelete?.();
          }}
          className="mt-1 p-1.5 rounded-md opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-500 hover:bg-red-50 transition-all shrink-0"
          title="Delete session"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      )}
    </div>
  );
}

function Section({
  title,
  rows,
  emptyText,
  activeId,
  onSelect,
  showProject,
  perPage,
}: {
  title: string;
  rows: SessionRow[];
  emptyText?: string;
  activeId?: string;
  onSelect: (id: string) => void;
  showProject?: boolean;
  perPage: number;
}) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(rows.length / perPage);
  // Clamp the page index when the row count shrinks (e.g. after a delete or refetch).
  const safePage = pageCount === 0 ? 0 : Math.min(page, pageCount - 1);
  const start = safePage * perPage;
  const pageRows = rows.slice(start, start + perPage);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5 px-1">
        <span className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{title}</span>
        <span className="text-xs bg-slate-100 text-gray-500 px-2 py-0.5 rounded-full">{rows.length}</span>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 overflow-hidden">
        {rows.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500">{emptyText ?? 'None'}</div>
        ) : (
          pageRows.map((row) => (
            <SessionRowItem
              key={row.id}
              row={row}
              active={row.id === activeId}
              onSelect={onSelect}
              showProject={showProject}
            />
          ))
        )}
      </div>
      {rows.length > perPage && <Pagination page={safePage} pageCount={pageCount} onPage={setPage} />}
    </div>
  );
}

export function SessionListPanel({
  sections,
  activeId,
  onSelect,
  onNewSession,
  newSessionLabel = 'New Session',
  showProject = false,
  loading = false,
  perPage = 5,
  emptyText,
  className = '',
}: SessionListPanelProps) {
  const allEmpty = sections.every((s) => s.rows.length === 0);

  return (
    <div className={`space-y-5 ${className}`}>
      {onNewSession && (
        <Button onClick={onNewSession} className="w-full">
          ＋ {newSessionLabel}
        </Button>
      )}

      {allEmpty ? (
        loading ? (
          <div className="flex justify-center py-8">
            <Spinner className="text-gray-400" />
          </div>
        ) : (
          <div className="text-sm text-gray-500 text-center py-8">
            {emptyText ?? 'No sessions yet.'}
          </div>
        )
      ) : (
        sections.map((section) => (
          <Section
            key={section.key}
            title={section.title}
            rows={section.rows}
            emptyText={section.emptyText}
            activeId={activeId}
            onSelect={onSelect}
            showProject={showProject}
            perPage={perPage}
          />
        ))
      )}
    </div>
  );
}
